// src/components/ProductPriceModal.js
import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import Button from '../../components/shared/Button';
import styled from 'styled-components';
import MaskedInput from '../../components/shared/MaskedInput';

const ModalContent = styled.div`
  padding: 20px;
  background-color: ${(props) => props.theme.colors.surface};
  border-radius: 12px;
  width: 100%;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
`;

const FieldWrapper = styled.div`
  position: relative;
  margin-bottom: 20px;
`;

const FloatingLabel = styled.label`
  position: absolute;
  top: ${(props) => (props.hasValue ? '2px' : '0')};
  left: 20px;
  transform: translateY(-20%);
  background-color: ${(props) => props.theme.colors.surface};
  color: ${(props) => (props.hasValue ? props.theme.colors.textPrimary : props.theme.colors.inputGray)};
  font-size: ${(props) => (props.hasValue ? '14px' : '14px')};
  padding: 0 5px;
  transition: all 0.2s ease;
`;

const AutocompleteInput = styled.input`
  width: 100%;
  padding: 20px;
  margin-top: 5px;
  border: 1px solid ${(props) => props.theme.colors.inputGray};
  background-color: ${(props) => props.theme.colors.surface};
  color: ${(props) => props.theme.colors.textPrimary};
  border-radius: 5px;
  font-size: 16px;
  outline: none;
`;

const AutocompleteList = styled.ul`
  margin-top: 5px;
  overflow-y: auto;
  padding: 0;
  list-style: none; /* Remove bullets */
  border: 1px solid ${(props) => props.theme.colors.inputBorder};
  border-radius: 5px;
  background-color: ${(props) => props.theme.colors.textPrimary};
  color:  ${(props) => props.theme.colors.background};
  position: relative;
  width: 100%;
  z-index: 1000;
`;

const AutocompleteListItem = styled.li`
  padding: 10px;
  cursor: pointer;
  &:hover {
    background-color: ${(props) => props.theme.colors.primary};
    color: white;
  }
`;

const StyledSelect  = styled.select`
  width: 100%;
  padding: 20px 20px;
  border: 1px solid ${(props) => props.theme.colors.inputGray};
  background-color: ${(props) => props.theme.colors.surface};
  color: ${(props) => props.theme.colors.textPrimary};
  border-radius: 5px;
  font-size: 16px;
  margin-top: 10px;
`;

const ProductPriceModal = ({ isOpen, onClose, onSave, products, existingProduct = {}, editMode = false }) => {
  const [selectedProduct, setSelectedProduct] = useState(''); // State for product name
  const [price, setPrice] = useState('0'); // State for price input
  const [unit, setUnit] = useState(''); // State for measurement unit
  const [filteredProducts, setFilteredProducts] = useState([]); // For autocomplete suggestions

  // Pre-fill when in edit mode, or reset when modal opens
  useEffect(() => {
    if (isOpen) {
      if (editMode && existingProduct) {
        setSelectedProduct(existingProduct.label); // Pre-fill the selected product name
        setPrice(existingProduct.price); // Pre-fill the price
        setUnit(existingProduct.unit || ''); // Pre-fill unit if available
      } else {
        setSelectedProduct('');
        setPrice(0);
        setUnit('');
      }
    }
  }, [isOpen, editMode, existingProduct]);

  // Filter products for autocomplete suggestions
  const handleProductInputChange = (e) => {
    const input = e.target.value.toLowerCase();
    setSelectedProduct(input);

    const filtered = products.filter(
      (prod) => prod.label && prod.label.toLowerCase().includes(input)
    );
    setFilteredProducts(filtered);
  };

  // Handle saving the selected product and price
  const handleSave = () => {
    if (!selectedProduct) return;
    const selected = products.find(
      (prod) => prod.label.toLowerCase() === selectedProduct.toLowerCase()
    );

    if (selected && price !== null && price !== undefined && price !== '') {
      onSave({
        id: selected.value,
        label: selected.label, // Send product ID and name
        price: parseFloat(price),
        unit, // Add the selected unit
        updatedAt: new Date().toISOString(),
      });
      onClose();
    }
  };

  const handleClose = () => {
    onClose();
  }

  // Hide the autocomplete list once a product is selected
  const handleProductSelect = (label) => {
    setSelectedProduct(label);
    setFilteredProducts([]); // Hide suggestions
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Add or Edit Product"
      style={{
        content: {
          width: '50%',
          height: '35%',
          margin: 'auto',
          backgroundColor: '#333',
        },
      }}
    >
      <ModalContent>
      <FieldWrapper>
          <FloatingLabel hasValue={!!selectedProduct}>Product Name</FloatingLabel>
          <AutocompleteInput
            type="text"
            value={selectedProduct}
            onChange={handleProductInputChange}
            placeholder="Start typing product name"
          />
          {filteredProducts.length > 0 && (
            <AutocompleteList>
              {filteredProducts.map((prod) => (
                <AutocompleteListItem
                  key={prod.value}
                  onClick={() => handleProductSelect(prod.label)} // Select product and hide list
                >
                  {prod.label}
                </AutocompleteListItem>
              ))}
            </AutocompleteList>
          )}
        </FieldWrapper>
        <MaskedInput
          label="Price"
          type="number"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder="Enter price"
          mask={"currency"}
        />
        <FieldWrapper>
          <FloatingLabel hasValue={!!unit}>Measurement Unit</FloatingLabel>
          <StyledSelect value={unit} onChange={(e) => setUnit(e.target.value)}>
            <option value="" disabled>Select unit</option>
            <option value="Yard">Yard</option>
            <option value="Ton">Ton</option>
            <option value="SqFt">Square Feet</option>
          </StyledSelect>
        </FieldWrapper>
        <ButtonsWrapper>
          <Button onClick={handleSave}>Save</Button>
          <Button onClick={onClose}>Close</Button>
        </ButtonsWrapper>
      </ModalContent>
    </Modal>
  );
};

export default ProductPriceModal;
